import { useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import { Box } from '@mui/material';
import EditableList from '../EditableList/EditableList';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import useEntertainmentType from '../../hooks/useEntertainmentType';

function UpgradeManagement() {
  const authCtx = useContext(AuthContext);
  const { getEntertainmentTypes } = useEntertainmentType();
  const [entertainmentTypes, setEntertainmentTypes] = useState([]);

  // Load entertainment types so we can render one image URL field per type for
  // the per entertainment-type package images (stored as a map on the upgrade).
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const types = await getEntertainmentTypes();
        if (active && Array.isArray(types)) setEntertainmentTypes(types);
      } catch (err) {
        // Field simply stays empty if types can't be loaded.
      }
    };
    load();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the nested schema: key = EntertainmentType _id, value = image URL/path.
  const picturesByEntertainmentTypeSchema = useMemo(() => {
    const schema = {};
    entertainmentTypes
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
      .forEach(type => {
        if (!type?._id) return;
        schema[type._id] = {
          label: type.name || type._id,
          type: 'text',
          required: false,
          placeholder: '/upgrade-images/kibu_laser.webp oder https://…',
        };
      });
    return schema;
  }, [entertainmentTypes]);

  if (!authCtx.checkRight('upgrade:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const upgradeSchema = {
    name: { label: 'Name', type: 'text', required: true, minWidth: '220px' },
    description: {
      label: 'Beschreibung',
      type: 'text',
      required: true,
      minWidth: '360px',
      previewInList: true,
      previewMaxLines: 3,
      useHyphens: true,
    },
    descriptionShort: {
      label: 'Kurzbeschreibung',
      type: 'text',
      required: false,
      minWidth: '280px',
    },
    internalDescription: {
      label: 'Interne Beschreibung',
      type: 'text',
      required: false,
      minWidth: '320px',
    },
    additionString: {
      label: 'Zusatztext Buchungsmail',
      type: 'text',
      required: false,
      minWidth: '360px',
      previewInList: true,
      previewMaxLines: 2,
    },
    type: {
      label: 'Typ',
      type: 'select',
      required: true,
      placeholder: 'Typ auswählen',
      options: [
        { label: 'matchPersons', value: 'matchPersons' },
        { label: 'matchPersonsNoCancel', value: 'matchPersonsNoCancel' },
        { label: 'matchLanesNoCancel', value: 'matchLanesNoCancel' },
        { label: 'freeSelect', value: 'freeSelect' },
        { label: 'freeSelectNoCancel', value: 'freeSelectNoCancel' },
        { label: 'freeSelectMaxPersons', value: 'freeSelectMaxPersons' },
      ],
    },
    minimumCount: { label: 'Mindestanzahl', type: 'number', required: false, minWidth: '140px' },
    maximumCount: { label: 'Maximale Anzahl', type: 'number', required: false, minWidth: '140px' },
    price: { label: 'Preis', type: 'price', required: true, minWidth: '140px' },
    validFrom: { label: 'Gültig ab', type: 'date', required: true, minWidth: '160px' },
    validTo: { label: 'Gültig bis', type: 'date', required: true, minWidth: '160px' },
    isActive: { label: 'Aktiv', type: 'boolean', required: false },
    isUserSelectable: { label: 'Benutzer wählbar', type: 'boolean', required: false },
    // Food-menu ("Speisekarte") fields. A menu item ("Speise") is just an upsell
    // assigned to a location (done in the Speisekarte view); everything shown on the
    // menu — image, category, allergens, order — is configured here on the upsell.
    picture: {
      label: 'Bild-URL',
      type: 'text',
      required: false,
      minWidth: '260px',
      placeholder: 'https://… oder /upgrade-images/pizza.webp',
    },
    category: {
      label: 'Kategorie (Speisekarte)',
      type: 'text',
      required: false,
      minWidth: '160px',
      placeholder: 'z.B. Pizza, Getränke, Beilagen',
    },
    allergens: {
      label: 'Allergene (kommagetrennt)',
      type: 'array',
      placeholder: 'z.B. Gluten, Laktose',
      displayValue: value => (Array.isArray(value) ? value.join(', ') : '—'),
      transformValue: value =>
        value
          .toString()
          .split(',')
          .map(v => v.trim())
          .filter(Boolean),
    },
    menuSortKey: {
      label: 'Sortierung (Speisekarte)',
      type: 'number',
      required: false,
      minWidth: '120px',
    },
    tags: {
      label: 'Tags (kommagetrennt)',
      type: 'array',
      placeholder: 'z.B. kids, party',
      displayValue: value => (Array.isArray(value) ? value.join(', ') : '—'),
      transformValue: value =>
        value
          .toString()
          .split(',')
          .map(v => v.trim())
          .filter(Boolean),
    },
    tagRules: {
      label: 'Tag-Regeln',
      type: 'object',
      excludeFromAdd: true,
      schema: {
        excludeOnTags: {
          label: 'Excludiere bei Tags (kommagetrennt)',
          type: 'array',
          placeholder: 'z.B. kids, party',
          displayValue: value => (Array.isArray(value) ? value.join(', ') : '—'),
          transformValue: value =>
            value
              .toString()
              .split(',')
              .map(v => v.trim())
              .filter(Boolean),
        },
        showOnTags: {
          label: 'Zeige bei Tags (kommagetrennt)',
          type: 'array',
          placeholder: 'z.B. kids, party',
          displayValue: value => (Array.isArray(value) ? value.join(', ') : '—'),
          transformValue: value =>
            value
              .toString()
              .split(',')
              .map(v => v.trim())
              .filter(Boolean),
        },
        forceOnTags: {
          label: 'Erzwinge bei Tags (kommagetrennt)',
          type: 'array',
          placeholder: 'z.B. kids, party',
          displayValue: value => (Array.isArray(value) ? value.join(', ') : '—'),
          transformValue: value =>
            value
              .toString()
              .split(',')
              .map(v => v.trim())
              .filter(Boolean),
        },
      },
    },
    includedUpgrades: {
      label: 'Inkludierte Upgrades (nur Pakete: Preis wird beim Paket abgespalten)',
      type: 'multi-select',
      excludeFromAdd: true,
      required: false,
      api: {
        resource: 'upgrade',
        labelField: 'name',
        valueField: '_id',
        skipSpecial: true,
      },
      placeholder: 'Upgrades auswählen',
      listDisplayValue: values => {
        if (!Array.isArray(values) || values.length === 0) return '—';
        return values
          .map(v => v?.name || v?.label || '')
          .filter(Boolean)
          .join(', ');
      },
    },
    displayAsPersonCategory: {
      label:
        'Im Buchungswidget als Personenkategorie anzeigen (nur Pakete; Preis = Basispreis + Paketpreis)',
      type: 'object',
      excludeFromAdd: true,
      schema: {
        shortName: { label: 'Kurzname (z.B. Premium)', type: 'text' },
        name: { label: 'Name (z.B. Premium-Spieler)', type: 'text' },
        hint: { label: 'Hinweis (unter dem Namen im Widget)', type: 'text' },
        sortKey: { label: 'Sortier-Reihenfolge (kleiner = oben)', type: 'number' },
        isBestseller: { label: 'Bestseller-Badge anzeigen', type: 'boolean' },
      },
    },
    // Per entertainment-type package image. Leave a field empty to fall back to
    // the upgrade's default image for that type. Only shown once entertainment
    // types have loaded.
    ...(Object.keys(picturesByEntertainmentTypeSchema).length > 0
      ? {
          picturesByEntertainmentType: {
            label: 'Bild pro Entertainment-Type (leer = Standardbild als Fallback)',
            type: 'object',
            excludeFromAdd: true,
            schema: picturesByEntertainmentTypeSchema,
          },
        }
      : {}),
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
    <EditableList
      schema={upgradeSchema}
      title='Neues Upgrade anlegen'
      resourceType='upgrade'
      headerSearchPlaceholder='Upgrades durchsuchen'
      parentIsEditable={authCtx.checkRight('upgrade:update')}
      options={{ titleKey: 'name' }}
    />
    </Box>
  );
}

export default UpgradeManagement;
